import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { PlusIcon, PencilSquareIcon, TrashIcon, ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/24/outline'
import apiClient from '../../services/apiClient'

// ─── Types ────────────────────────────────────────────────────────────────────
interface Workshop {
  id: number
  slug: string
  title: string
  description: string | null
  thumbnail_url: string | null
  held_at: string
  duration_min: number | null
  youtube_url: string | null
  status: 'upcoming' | 'completed'
  is_published: number
  position: number
  material_count: number
  whiteboard_count: number
}

interface WorkshopFormData {
  slug: string
  title: string
  description: string
  held_at: string
  duration_min: string
  youtube_url: string
  status: 'upcoming' | 'completed'
  is_published: boolean
  position: string
}

const EMPTY_FORM: WorkshopFormData = {
  slug: '', title: '', description: '',
  held_at: '', duration_min: '', youtube_url: '',
  status: 'completed', is_published: false, position: '0',
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(d: string) {
  try {
    return new Date(d).toLocaleDateString('vi-VN', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  } catch { return d }
}

function formatHeldAtForInput(d: string): string {
  try {
    const dt = new Date(d)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`
  } catch { return '' }
}

// ─── Workshop Form Modal ───────────────────────────────────────────────────────
function WorkshopFormModal({
  initial,
  onClose,
  onSaved,
}: {
  initial: Workshop | null
  onClose: () => void
  onSaved: () => void
}) {
  const isEdit = Boolean(initial)
  const [form, setForm] = useState<WorkshopFormData>(() =>
    initial
      ? {
          slug: initial.slug,
          title: initial.title,
          description: initial.description ?? '',
          held_at: formatHeldAtForInput(initial.held_at),
          duration_min: initial.duration_min != null ? String(initial.duration_min) : '',
          youtube_url: initial.youtube_url ?? '',
          status: initial.status,
          is_published: Boolean(initial.is_published),
          position: String(initial.position),
        }
      : EMPTY_FORM
  )
  const [saving, setSaving] = useState(false)

  const handleChange = (field: keyof WorkshopFormData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.slug || !form.title || !form.held_at) {
      toast.error('Vui lòng điền slug, tiêu đề và thời gian.')
      return
    }
    setSaving(true)
    try {
      const payload = {
        slug: form.slug.trim(),
        title: form.title.trim(),
        description: form.description.trim() || null,
        held_at: form.held_at,
        duration_min: form.duration_min ? parseInt(form.duration_min, 10) : null,
        youtube_url: form.youtube_url.trim() || null,
        status: form.status,
        is_published: form.is_published,
        position: parseInt(form.position, 10) || 0,
      }
      if (isEdit) {
        await apiClient.put(`/admin/workshops/${initial!.slug}`, payload)
        toast.success('Đã cập nhật workshop')
      } else {
        await apiClient.post('/admin/workshops', payload)
        toast.success('Đã tạo workshop mới')
      }
      onSaved()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Lưu thất bại')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-y-auto p-4 pt-10">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-bold text-lg text-m2-primary">
            {isEdit ? 'Chỉnh sửa Workshop' : 'Tạo Workshop mới'}
          </h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-m2-surface-low">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={(e) => void handleSubmit(e)} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1 text-m2-on-surface">Slug *</label>
              <input
                value={form.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                placeholder="ai4sdlc-2026-09-27"
                disabled={isEdit}
                className="w-full rounded-lg border border-m2-outline px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary disabled:bg-m2-surface-low disabled:text-m2-on-surface-variant"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 text-m2-on-surface">Vị trí (position)</label>
              <input
                type="number"
                value={form.position}
                onChange={(e) => handleChange('position', e.target.value)}
                className="w-full rounded-lg border border-m2-outline px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1 text-m2-on-surface">Tiêu đề *</label>
            <input
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="AI4SDLC Workshop"
              className="w-full rounded-lg border border-m2-outline px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1 text-m2-on-surface">Mô tả</label>
            <textarea
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-m2-outline px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1 text-m2-on-surface">Thời gian diễn ra *</label>
              <input
                type="datetime-local"
                value={form.held_at}
                onChange={(e) => handleChange('held_at', e.target.value)}
                className="w-full rounded-lg border border-m2-outline px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 text-m2-on-surface">Thời lượng (phút)</label>
              <input
                type="number"
                value={form.duration_min}
                onChange={(e) => handleChange('duration_min', e.target.value)}
                placeholder="120"
                className="w-full rounded-lg border border-m2-outline px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1 text-m2-on-surface">YouTube URL</label>
            <input
              value={form.youtube_url}
              onChange={(e) => handleChange('youtube_url', e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              className="w-full rounded-lg border border-m2-outline px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1 text-m2-on-surface">Trạng thái</label>
              <select
                value={form.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full rounded-lg border border-m2-outline px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
              >
                <option value="upcoming">Sắp diễn ra</option>
                <option value="completed">Đã diễn ra</option>
              </select>
            </div>
            <div className="flex items-center gap-3 pt-5">
              <input
                type="checkbox"
                id="is_published"
                checked={form.is_published}
                onChange={(e) => handleChange('is_published', e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <label htmlFor="is_published" className="text-sm font-medium text-m2-on-surface">
                Hiển thị công khai
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-m2-outline-variant hover:bg-m2-surface-low"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 text-sm rounded-lg bg-m2-primary text-white hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Đang lưu…' : isEdit ? 'Cập nhật' : 'Tạo mới'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Upload Material Modal ─────────────────────────────────────────────────────
function UploadMaterialModal({
  workshopId,
  onClose,
  onUploaded,
}: {
  workshopId: number
  onClose: () => void
  onUploaded: () => void
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [title, setTitle] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file || !title.trim()) {
      toast.error('Vui lòng chọn file và nhập tiêu đề.')
      return
    }
    setUploading(true)
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('title', title.trim())
      await apiClient.post(`/admin/workshops/${workshopId}/materials`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      })
      toast.success('Upload tài liệu thành công!')
      onUploaded()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Upload thất bại')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-bold text-base text-m2-primary">Upload tài liệu</h3>
          <button type="button" onClick={onClose}><XMarkIcon className="w-5 h-5" /></button>
        </div>
        <form onSubmit={(e) => void handleUpload(e)} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold mb-1">Tiêu đề tài liệu *</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Slide Workshop AI4SDLC"
              className="w-full rounded-lg border border-m2-outline px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-m2-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">File (PDF, PPTX, HTML, DOCX, ZIP)</label>
            <div
              className="border-2 border-dashed border-m2-outline rounded-xl p-6 text-center cursor-pointer hover:border-m2-primary transition-colors"
              onClick={() => fileRef.current?.click()}
            >
              {file ? (
                <p className="text-sm font-medium text-m2-on-surface">{file.name}</p>
              ) : (
                <>
                  <ArrowUpTrayIcon className="w-8 h-8 mx-auto mb-2 text-m2-on-surface-variant" />
                  <p className="text-sm text-m2-on-surface-variant">Click để chọn file</p>
                </>
              )}
              <input
                type="file"
                ref={fileRef}
                className="hidden"
                accept=".pdf,.pptx,.ppt,.html,.htm,.docx,.zip"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-m2-outline-variant hover:bg-m2-surface-low">Hủy</button>
            <button type="submit" disabled={uploading} className="px-5 py-2 text-sm rounded-lg bg-m2-primary text-white hover:opacity-90 disabled:opacity-50">
              {uploading ? 'Đang upload…' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────
const AdminWorkshopsPage = () => {
  const [workshops, setWorkshops] = useState<Workshop[]>([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Workshop | null>(null)
  const [uploadTarget, setUploadTarget] = useState<Workshop | null>(null)

  const loadWorkshops = useCallback(async () => {
    setLoading(true)
    try {
      const res = await apiClient.get<{ success: boolean; data: Workshop[] }>('/admin/workshops')
      if (res.data?.success) setWorkshops(res.data.data)
    } catch {
      toast.error('Không tải được danh sách workshop')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void loadWorkshops() }, [loadWorkshops])

  const handleDelete = async (w: Workshop) => {
    if (!confirm(`Xóa workshop "${w.title}"? Hành động này không thể hoàn tác.`)) return
    try {
      // Note: DELETE endpoint not yet implemented (add if needed)
      toast.info('Chức năng xóa chưa được triển khai. Dùng tắt is_published để ẩn.')
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Xóa thất bại')
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-m2-primary">Quản lý Workshop</h1>
          <p className="text-sm text-m2-on-surface-variant mt-1">
            Tạo, chỉnh sửa workshop và upload tài liệu đính kèm.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setEditTarget(null); setFormOpen(true) }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-m2-primary text-m2-on-primary text-sm font-semibold hover:opacity-90 shadow-sm"
        >
          <PlusIcon className="w-4 h-4" />
          Tạo Workshop
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded-xl bg-m2-surface-low border border-m2-outline-variant animate-pulse" />
          ))}
        </div>
      ) : workshops.length === 0 ? (
        <div className="text-center py-20 text-m2-on-surface-variant">
          <p className="text-4xl mb-4">🎓</p>
          <p>Chưa có workshop nào. Nhấn "Tạo Workshop" để bắt đầu.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-m2-outline-variant shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-m2-outline-variant">
                <th className="px-4 py-3 font-semibold">Workshop</th>
                <th className="px-4 py-3 font-semibold">Thời gian</th>
                <th className="px-4 py-3 font-semibold text-center">Trạng thái</th>
                <th className="px-4 py-3 font-semibold text-center">Tài liệu</th>
                <th className="px-4 py-3 font-semibold text-center">Bảng vẽ</th>
                <th className="px-4 py-3 font-semibold text-center">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {workshops.map((w) => (
                <tr key={w.id} className="border-b border-m2-outline-variant last:border-0 hover:bg-m2-surface-low transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-m2-on-surface">{w.title}</p>
                    <p className="text-xs text-m2-on-surface-variant font-mono">{w.slug}</p>
                  </td>
                  <td className="px-4 py-3 text-m2-on-surface-variant text-xs">{formatDate(w.held_at)}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex flex-col items-center gap-1">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                        w.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {w.status === 'completed' ? 'Đã diễn ra' : 'Sắp tới'}
                      </span>
                      {!w.is_published && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-gray-100 text-gray-500">Ẩn</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center text-m2-on-surface-variant">
                    {w.material_count}
                  </td>
                  <td className="px-4 py-3 text-center text-m2-on-surface-variant">
                    {w.whiteboard_count}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <Link
                        to={`/workshops/${w.slug}`}
                        target="_blank"
                        className="px-2.5 py-1.5 rounded-lg border border-m2-outline-variant text-xs text-m2-primary hover:bg-m2-surface-low"
                        title="Xem trang"
                      >
                        Xem
                      </Link>
                      <button
                        type="button"
                        onClick={() => setUploadTarget(w)}
                        className="p-1.5 rounded-lg border border-m2-outline-variant hover:bg-m2-surface-low text-m2-on-surface-variant"
                        title="Upload tài liệu"
                      >
                        <ArrowUpTrayIcon className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => { setEditTarget(w); setFormOpen(true) }}
                        className="p-1.5 rounded-lg border border-m2-outline-variant hover:bg-m2-surface-low text-m2-on-surface-variant"
                        title="Chỉnh sửa"
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(w)}
                        className="p-1.5 rounded-lg border border-red-200 hover:bg-red-50 text-red-500"
                        title="Xóa"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {formOpen && (
        <WorkshopFormModal
          initial={editTarget}
          onClose={() => { setFormOpen(false); setEditTarget(null) }}
          onSaved={() => { setFormOpen(false); setEditTarget(null); void loadWorkshops() }}
        />
      )}

      {uploadTarget && (
        <UploadMaterialModal
          workshopId={uploadTarget.id}
          onClose={() => setUploadTarget(null)}
          onUploaded={() => { setUploadTarget(null); void loadWorkshops() }}
        />
      )}
    </div>
  )
}

export default AdminWorkshopsPage
